import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import { formatDate, formatNombre } from '../../lib/formats';
import { cn, formatVolume } from '../../lib/utils';
import { usePagination } from '../../hooks/usePagination';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { BarrePagination } from '../ui/pagination-bar';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';

const nombre = (valeur) => formatNombre(valeur, 0, '—');

/** Un montant: aligné à droite, rouge s'il est négatif. */
const Chiffre = ({ valeur, gras = false, discret = false, className }) => (
  <TableCell
    className={cn(
      'whitespace-nowrap text-right tabular-nums',
      Number(valeur) < 0 && 'text-destructive',
      gras && 'font-semibold',
      discret && 'text-muted-foreground',
      className,
    )}
  >
    {nombre(valeur)}
  </TableCell>
);

/** Les pastilles d'une ligne: ce qui reste à revoir, sans phrase. */
const Pastilles = ({ ligne }) => (
  <div className="flex flex-wrap gap-1">
    {ligne.quantity_estimated ? <Badge variant="warning">Volume estimé</Badge> : null}
    {ligne.prix === null || ligne.prix === undefined ? <Badge variant="warning">Prix à saisir</Badge> : null}
    {ligne.orpheline ? <Badge variant="destructive">Plus proposé</Badge> : null}
  </div>
);

/**
 * Les trajets dépotés qu'un état peut porter: case à cocher, prix modifiable
 * ligne par ligne (ou pour toutes), dépoté à saisir quand le manquant est
 * inconnu, montants en aperçu tant que l'état n'est pas enregistré.
 */
const TransportStatementTrips = ({
  lignes,
  saisie,
  totaux,
  disabled = false,
  onBasculer,
  onBasculerTous,
  onPrix,
  onLivre,
  onAppliquerPrix,
}) => {
  const [recherche, setRecherche] = useState('');
  const [prixTous, setPrixTous] = useState('');
  const [selectionSeule, setSelectionSeule] = useState(false);

  const filtrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return lignes.filter((ligne) => {
      if (selectionSeule && !saisie.selectionnes.has(ligne.trip_id)) return false;
      if (!terme) return true;
      return [ligne.tractor_plate, ligne.tank_plate].some((texte) => String(texte || '').toLowerCase().includes(terme));
    });
  }, [lignes, recherche, selectionSeule, saisie.selectionnes]);

  const pagination = usePagination(filtrees, {
    cle: 'trajets',
    reinitialiser: `${recherche}|${selectionSeule}`,
  });

  const choisies = saisie.selectionnes.size;
  const toutesChoisies = filtrees.length > 0 && filtrees.every((ligne) => saisie.selectionnes.has(ligne.trip_id));
  const certainesChoisies = filtrees.some((ligne) => saisie.selectionnes.has(ligne.trip_id));
  const etatCaseTous = toutesChoisies ? true : certainesChoisies ? 'indeterminate' : false;

  return (
    <section className="space-y-2" aria-label="Trajets de l’état" data-testid="etat-trajets">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold text-foreground">Trajets dépotés</h2>
        <Badge variant={choisies > 0 ? 'info' : 'muted'} data-testid="etat-compteur">
          {choisies} sur {lignes.length}
        </Badge>
        <div className="relative w-48">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
            placeholder="Tracteur ou citerne"
            aria-label="Rechercher un tracteur ou une citerne"
            className="h-8 pl-8 text-xs"
            data-testid="etat-recherche"
          />
        </div>
        <Button
          type="button"
          variant={selectionSeule ? 'default' : 'outline'}
          size="sm"
          aria-pressed={selectionSeule}
          onClick={() => setSelectionSeule((actif) => !actif)}
          className="h-8 text-xs"
          data-testid="etat-selection-seule"
        >
          Choisis
        </Button>
        <div className="ml-auto flex items-center gap-1.5">
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={prixTous}
            onChange={(event) => setPrixTous(event.target.value)}
            placeholder="Prix au litre"
            aria-label="Prix au litre pour toutes les lignes"
            disabled={disabled}
            className="h-8 w-28 text-right text-xs tabular-nums"
            data-testid="etat-prix-toutes"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            disabled={disabled || prixTous === ''}
            onClick={() => onAppliquerPrix(prixTous)}
            data-testid="etat-appliquer-prix"
          >
            Appliquer à toutes
          </Button>
        </div>
      </div>

      {lignes.length === 0 ? (
        <EmptyState
          illustration="donnee"
          title="Aucun trajet dépoté à régler"
          description="Les trajets déjà portés par un état n’apparaissent plus ici."
        />
      ) : filtrees.length === 0 ? (
        <EmptyState variant="filtered" title="Aucun trajet ne correspond" description="Modifiez la recherche." />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table stickyFirstColumn={false} data-testid="etat-table-trajets">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={etatCaseTous}
                      disabled={disabled}
                      onCheckedChange={(valeur) => onBasculerTous(filtrees.map((l) => l.trip_id), valeur === true)}
                      aria-label="Choisir tous les trajets"
                      data-testid="etat-choisir-tous"
                    />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Tracteur</TableHead>
                  <TableHead>Citerne</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead className="text-right">Prix</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-right">Manquant</TableHead>
                  <TableHead className="text-right">Retenue</TableHead>
                  <TableHead className="text-right">Dépense</TableHead>
                  <TableHead className="text-right">Solde</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((ligne) => {
                  const choisi = saisie.selectionnes.has(ligne.trip_id);
                  const discret = !choisi;
                  return (
                    <TableRow
                      key={ligne.trip_id}
                      data-state={choisi ? 'selected' : undefined}
                      data-testid={`etat-ligne-${ligne.trip_id}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={choisi}
                          disabled={disabled}
                          onCheckedChange={() => onBasculer(ligne.trip_id)}
                          aria-label={`Choisir le trajet du tracteur ${ligne.tractor_plate || ''}`.trim()}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{formatDate(ligne.line_date)}</TableCell>
                      <TableCell className="whitespace-nowrap font-semibold">{ligne.tractor_plate || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{ligne.tank_plate || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        {formatVolume(ligne.quantity)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="any"
                          value={saisie.prix[ligne.trip_id] ?? ''}
                          disabled={disabled}
                          onChange={(event) => onPrix(ligne.trip_id, event.target.value)}
                          aria-label={`Prix au litre du tracteur ${ligne.tractor_plate || ''}`.trim()}
                          className="ml-auto h-8 w-24 text-right tabular-nums"
                        />
                      </TableCell>
                      <Chiffre valeur={ligne.montant} discret={discret} />
                      <TableCell className="text-right tabular-nums">
                        {ligne.shortage_known ? (
                          <span className={cn(discret && 'text-muted-foreground')}>{formatVolume(ligne.manquant)}</span>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5">
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="any"
                              value={saisie.livre[ligne.trip_id] ?? ''}
                              disabled={disabled}
                              onChange={(event) => onLivre(ligne.trip_id, event.target.value)}
                              placeholder="Dépoté (L)"
                              aria-label={`Dépoté du tracteur ${ligne.tractor_plate || ''}`.trim()}
                              className="h-8 w-28 text-right tabular-nums"
                            />
                            {!ligne.manquantInconnu ? (
                              <span className="text-xs text-muted-foreground">{formatVolume(ligne.manquant)}</span>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={cn('whitespace-nowrap text-right tabular-nums', discret && 'text-muted-foreground')}>
                        {ligne.already_retained ? (
                          <Badge variant="muted">Déjà retenu</Badge>
                        ) : (
                          <div className="flex flex-col items-end">
                            <span>{nombre(ligne.retenue)}</span>
                            {ligne.litresRetenus ? (
                              <span className="text-xs text-muted-foreground">{formatVolume(ligne.litresRetenus)}</span>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      <Chiffre valeur={ligne.depense} discret={discret} />
                      <Chiffre valeur={ligne.solde} gras discret={discret} />
                      <TableCell>
                        <Pastilles ligne={ligne} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow className="font-bold" data-testid="etat-totaux">
                  <TableCell />
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(totaux.quantite)}</TableCell>
                  <TableCell />
                  <Chiffre valeur={totaux.montant} />
                  <TableCell />
                  <Chiffre valeur={totaux.retenue} />
                  <Chiffre valeur={totaux.depense} />
                  <Chiffre valeur={totaux.solde} />
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <BarrePagination pagination={pagination} nom="trajets" />
        </>
      )}
    </section>
  );
};

export default TransportStatementTrips;
